import { addDays, differenceInDays } from 'date-fns';

import useMostRecentAccountItem from ':cloud/hooks/useMostRecentAccountItem';
import {
  TRIAL_EXPIRY_WARNING_LENGTH,
  WAAS_TRIAL_LENGTH,
} from ':cloud/pages/WaasProjects/constants';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { getFormattedDateExtended } from ':cloud/utils/date';

const FreeTrialStatuses = ['trial', 'trial-expired', 'trial-near-expiry'] as const;

type FreeTrialStatus = (typeof FreeTrialStatuses)[number];
type PaidSubscriptionStatus = 'active' | 'canceled';
type SubscriptionStatus = FreeTrialStatus | PaidSubscriptionStatus | 'none';

export function isFreeTrial(status: SubscriptionStatus): status is FreeTrialStatus {
  return FreeTrialStatuses.includes(status as FreeTrialStatus);
}

export type SubscriptionState = {
  status: SubscriptionStatus;
  title?: string;
  messageText?: string;
  shortMessageText?: string;
};

function useSubscriptionState(): SubscriptionState {
  const { activeOrg } = useGetUser();
  const { projects } = useGetProjects(activeOrg?.organizationId);
  const mostRecentPaidSubscription = useMostRecentAccountItem();

  if (!projects?.length) {
    return {
      status: 'none',
    };
  }

  // same as days since project creation
  const trialCreationDate = new Date(projects[0].createdAt);
  const daysSinceTrialCreation = differenceInDays(new Date(), trialCreationDate);
  const daysRemainingInTrial = Math.max(0, WAAS_TRIAL_LENGTH - daysSinceTrialCreation);
  const expirationDate = addDays(trialCreationDate, WAAS_TRIAL_LENGTH);

  if (mostRecentPaidSubscription) {
    // canceled subscription
    if (mostRecentPaidSubscription?.cancelTime) {
      const formattedCancelDate = getFormattedDateExtended(
        new Date(mostRecentPaidSubscription.cancelTime),
      );
      return {
        status: 'canceled',
        title: 'Canceled',
        messageText: `Your subscription was canceled on ${formattedCancelDate}`,
        shortMessageText: formattedCancelDate,
      };
    }
    return {
      status: 'active',
    };
  }

  // expired trial
  if (daysSinceTrialCreation >= WAAS_TRIAL_LENGTH) {
    return {
      status: 'trial-expired',
      title: 'Expired',
      messageText: `Your ${WAAS_TRIAL_LENGTH}-day free trial has ended.`,
      shortMessageText: `${getFormattedDateExtended(expirationDate)}`,
    };
  }

  // nearing trial expiry
  if (daysRemainingInTrial <= TRIAL_EXPIRY_WARNING_LENGTH) {
    let messageText = `Your ${WAAS_TRIAL_LENGTH}-day free trial ends in ${daysRemainingInTrial} days.`;
    let shortMessageText = `Ends in ${daysRemainingInTrial} days`;
    if (daysRemainingInTrial === 1) {
      messageText = `Your ${WAAS_TRIAL_LENGTH}-day free trial ends in 1 day.`;
      shortMessageText = 'Ends in 1 day';
    }
    if (daysRemainingInTrial === 0) {
      messageText = `Your ${WAAS_TRIAL_LENGTH}-day free trial ends in less than a day.`;
      shortMessageText = 'Ends in less than a day';
    }
    return {
      status: 'trial-near-expiry',
      title: `${WAAS_TRIAL_LENGTH}-day free trial`,
      messageText,
      shortMessageText,
    };
  }

  return {
    status: 'trial',
    title: `${WAAS_TRIAL_LENGTH}-day free trial`,
    shortMessageText: `Free trial ends on ${getFormattedDateExtended(expirationDate)}`,
  };
}

export default useSubscriptionState;
